import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Switch } from "antd";
import {
  setAllowAnalytics,
  setInitialized,
  setShowBanner,
} from "../../store/cookies/cookieConsentSlice";

const COOKIE_EXPIRY_DAYS = 90;
const COOKIE_NAME = "cookieConsent";

const CookieBanner = () => {
  const dispatch = useDispatch();

  const allowAnalytics = useSelector(
    (state) => state.cookieConsent.allowAnalytics,
  );
  const initialized = useSelector((state) => state.cookieConsent.initialized);
  const showBannerFromRedux = useSelector(
    (state) => state.cookieConsent.showBanner,
  );

  // Helper function to check cookie (used for initial state)
  const getCookieInitial = (name) => {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2].trim()) : null;
  };

  // Initialize visibility based on cookie existence
  const checkInitialCookie = () => {
    const stored = getCookieInitial(COOKIE_NAME);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const { timestamp, analytics } = parsed;
        const now = Date.now();
        const expiry = COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (now - timestamp <= expiry) {
          return false; // Cookie exists and is valid, don't show banner
        }
      } catch {
        // Invalid cookie, show banner
      }
    }
    return true; // No cookie or expired, show banner
  };

  const [isVisible, setIsVisible] = useState(checkInitialCookie);

  // Utility functions for cookies
  const setCookie = (name, value, days) => {
    const expires = new Date(
      Date.now() + days * 24 * 60 * 60 * 1000,
    ).toUTCString();
    // Check if we're in an iframe (cross-origin context)
    const isInIframe = window.self !== window.top;
    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost";

    // For iframe contexts, use SameSite=None with Secure (requires HTTPS)
    // For same-origin, use Lax
    let cookieString = `${name}=${encodeURIComponent(
      value,
    )}; expires=${expires}; path=/`;

    if (isInIframe && isSecure) {
      cookieString += "; SameSite=None; Secure";
    } else if (!isInIframe) {
      cookieString += "; SameSite=Lax";
    }

    document.cookie = cookieString;
  };

  const getCookie = (name) => {
    // Improved regex to handle cookies with spaces and special characters
    const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2].trim()) : null;
  };

  const deleteCookie = (name) => {
    const isInIframe = window.self !== window.top;
    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost";

    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    if (isInIframe && isSecure) {
      cookieString += "; SameSite=None; Secure";
    } else if (!isInIframe) {
      cookieString += "; SameSite=Lax";
    }

    document.cookie = cookieString;
  };

  const waitForGtagAndConfigure = (allow, retries = 10) => {
    if (typeof window.gtag === "function") {
      try {
        if (allow) {
          window.gtag("config", "G-319Q2X9CYE", { send_page_view: true });
          window.gtag("consent", "update", { analytics_storage: "granted" });
        } else {
          window.gtag("consent", "update", { analytics_storage: "denied" });
        }
      } catch (err) {
        console.error("[gtag] Error calling gtag:", err);
      }
    } else if (retries > 0) {
      setTimeout(() => waitForGtagAndConfigure(allow, retries - 1), 300);
    }
  };

  const saveConsent = (value) => {
    try {
      const data = JSON.stringify({
        timestamp: Date.now(),
        analytics: value,
      });
      setCookie(COOKIE_NAME, data, COOKIE_EXPIRY_DAYS);
      dispatch(setAllowAnalytics(value));
      setIsVisible(false);
      dispatch(setShowBanner(false));
      waitForGtagAndConfigure(value);
    } catch {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    // Always check cookie on mount (important for iframe reloads where Redux state resets)
    const checkCookie = () => {
      const stored = getCookie(COOKIE_NAME);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const { timestamp, analytics } = parsed;
          const now = Date.now();
          const expiry = COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

          if (now - timestamp > expiry) {
            // Cookie expired
            deleteCookie(COOKIE_NAME);
            setIsVisible(true);
            dispatch(setShowBanner(false));
          } else {
            // Valid cookie exists - hide banner and sync Redux state
            dispatch(setAllowAnalytics(analytics));
            setIsVisible(false);
            dispatch(setShowBanner(false));
            if (analytics) setTimeout(() => waitForGtagAndConfigure(true), 100);
          }
        } catch (err) {
          console.error("Error parsing cookie consent:", err);
          deleteCookie(COOKIE_NAME);
          setIsVisible(true);
          dispatch(setShowBanner(false));
        }
      } else {
        // No cookie - show banner (iframe reloads reset Redux state, so always check cookie)
        setIsVisible(true);
      }
    };

    // Check cookie immediately
    checkCookie();

    // Mark as initialized to prevent unnecessary re-checks
    if (!initialized) {
      dispatch(setInitialized(true));
    }
  }, [dispatch, initialized]);

  useEffect(() => {
    if (showBannerFromRedux) {
      setIsVisible(true);
    }
  }, [showBannerFromRedux]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl p-4 bg-white border border-gray-200 shadow-md flex flex-col items-center gap-5 rounded-2xl">
          <span className="font-semibold text-lg">Cookies Consent</span>
          <span className="text-sm">
            You can change your settings at any time by clicking "Cookie
            Settings" at the bottom of the home page.
          </span>
          <div className="flex flex-col md:flex-row gap-3 md:gap-5 items-start w-full test">
            <span className="w-full md:w-auto text-sm text-gray-700 text-left">
              <b>Analytics</b> - Enabling analytics cookies helps us understand
              how you use this site and improve your experience.
            </span>
            <div className="w-full md:w-auto flex items-center justify-start gap-2">
              <Switch
                checked={allowAnalytics}
                onChange={(checked) => dispatch(setAllowAnalytics(checked))}
              />
              <span>{allowAnalytics ? "Yes" : "No"}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row w-full gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => saveConsent(true)}
              type="primary"
              className="w-full sm:w-auto bg-blue hover:bg-[#ff4a1a] text-white rounded-2xl border-none px-6 py-3 font-bold"
            >
              Accept All
            </Button>
            <Button
              onClick={() => saveConsent(false)}
              className="w-full sm:w-auto bg-gray-200 hover:bg-[#ff4a1a] text-black rounded-2xl border-none px-6 py-3 font-bold"
            >
              Reject All
            </Button>
            <Button
              onClick={() => saveConsent(allowAnalytics)}
              className="w-full sm:w-auto bg-blue-100 hover:bg-blue-200 text-black rounded-2xl border-none px-6 py-3 font-bold"
            >
              Save Preferences
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
